/// <reference types="vite/client" />
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

let authTokenGetter: (() => string | null) | null = null;
let tokenRefresher: (() => Promise<string>) | null = null;
/** After login: persisted `tenantId` for `X-Tenant-Id` on `/api/v1/*` (not injected on login/refresh — those use body slug or explicit headers). */
let tenantIdGetter: (() => string | null) | null = null;
let warnedMissingTenant = false;

export function configureApiClient(opts: {
  getToken: () => string | null;
  refreshToken: () => Promise<string>;
  getTenantId?: () => string | null;
}) {
  authTokenGetter = opts.getToken;
  tokenRefresher  = opts.refreshToken;
  tenantIdGetter  = opts.getTenantId ?? null;
}

function resolveApiBaseUrl(): string {
  const viteBase =
    typeof import.meta !== 'undefined' ? import.meta.env?.VITE_API_BASE_URL : undefined;
  if (viteBase != null && String(viteBase).trim() !== '') {
    return String(viteBase).trim();
  }
  // Dev default: direct to local API on port 3000 (no Vite `/api` proxy when `VITE_API_BASE_URL` is absolute in `.env`).
  // API must allow CORS from the Vite page origin (e.g. http://localhost:8002).
  // To use the Vite proxy instead: set `VITE_API_BASE_URL=/api/v1` in `.env.local`.
  if (typeof import.meta !== 'undefined' && import.meta.env.DEV) {
    return 'http://127.0.0.1:3000/api/v1';
  }
  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  return '/api/v1';
}

function resolveTimeoutMs(): number {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_TIMEOUT_MS) {
    const n = Number(import.meta.env.VITE_API_TIMEOUT_MS);
    if (Number.isFinite(n) && n > 0) return n;
  }
  // Local APIs (DB cold start, etc.) often exceed 15s; production builds keep a tighter default.
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) return 60_000;
  return 15_000;
}

const apiClient: AxiosInstance = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: resolveTimeoutMs(),
  headers: { 'Content-Type': 'application/json' },
});

/** Pull human-readable text from Nest / envelope / validation error bodies. */
function extractServerMessage(data: unknown): string | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const o = data as Record<string, unknown>;
  const msg = o.message;
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (Array.isArray(msg) && msg.every((x) => typeof x === 'string')) return (msg as string[]).join(' ');
  const nested = o.error;
  if (nested && typeof nested === 'object') {
    const em = (nested as Record<string, unknown>).message;
    if (typeof em === 'string' && em.trim()) return em;
    if (Array.isArray(em) && em.every((x) => typeof x === 'string')) return (em as string[]).join(' ');
  }
  return undefined;
}

function extractServerCode(data: unknown): string | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const o = data as Record<string, unknown>;
  if (typeof o.code === 'string' && o.code.trim()) return o.code;
  if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
  const nested = o.error;
  if (nested && typeof nested === 'object' && typeof (nested as Record<string, unknown>).code === 'string') {
    return (nested as Record<string, unknown>).code as string;
  }
  return undefined;
}

/** Resolved request URL fragment for header / auth rules (handles absolute `config.url`). */
function resolvedRequestUrl(config: InternalAxiosRequestConfig): string {
  const u = String(config.url ?? '');
  if (/^https?:\/\//i.test(u)) return u;
  return `${config.baseURL ?? ''}${u}`;
}

/** BFF routes under `/internal/*` use `X-Internal-Token`, not user JWT — do not send `Authorization: Bearer`. */
function isInternalApiPath(config: InternalAxiosRequestConfig): boolean {
  return resolvedRequestUrl(config).includes('/internal/');
}

/** Do not chain refresh+retry on these — e.g. 401 from bad password must not call `/auth/refresh`. */
function isPublicAuthPath(config: InternalAxiosRequestConfig): boolean {
  const url = resolvedRequestUrl(config);
  return /\/auth\/(login|refresh|logout)(\?|$)/i.test(url);
}

/** True if the outgoing request already sets a tenant (e.g. owner-invite login). */
function requestAlreadyHasTenantId(config: InternalAxiosRequestConfig): boolean {
  const h = config.headers;
  if (!h || typeof h !== 'object') return false;
  const maybeGet = (h as { get?: (name: string) => unknown }).get;
  if (typeof maybeGet === 'function') {
    const v = maybeGet.call(h, 'X-Tenant-Id') ?? maybeGet.call(h, 'x-tenant-id');
    if (typeof v === 'string' && v.trim()) return true;
  }
  const headers = h as Record<string, string | number | boolean | undefined>;
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() !== 'x-tenant-id') continue;
    const v = headers[key];
    if (typeof v === 'string' && v.trim()) return true;
  }
  return false;
}

function isAuthLoginOrRefreshUrl(url: string): boolean {
  return /\/auth\/(login|refresh)(\?|$)/i.test(url);
}

/** True when login JSON body includes a non-empty `workspaceSlug` (valid tenant locator without header). */
function loginBodyHasWorkspaceSlug(config: InternalAxiosRequestConfig): boolean {
  const d = config.data;
  if (d == null) return false;
  let o: Record<string, unknown>;
  if (typeof d === 'string') {
    try {
      o = JSON.parse(d) as Record<string, unknown>;
    } catch {
      return false;
    }
  } else if (typeof d === 'object') {
    o = d as Record<string, unknown>;
  } else {
    return false;
  }
  const s = o.workspaceSlug;
  return typeof s === 'string' && s.trim().length > 0;
}

// Request — attach token + optional headers (tenant / gateway keys)
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const internal = isInternalApiPath(config);
  const token = authTokenGetter?.();
  if (token && !internal) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (internal) {
    const h = config.headers;
    if (h && typeof (h as { delete?: (n: string) => void }).delete === 'function') {
      (h as { delete: (n: string) => void }).delete('Authorization');
      (h as { delete: (n: string) => void }).delete('authorization');
    } else if (h && typeof h === 'object') {
      delete (h as Record<string, unknown>).Authorization;
      delete (h as Record<string, unknown>).authorization;
    }
    // Dev: `vite.config` bridges `INTERNAL_API_SECRET` → `import.meta.env.VITE_DEV_INTERNAL_API_TOKEN`.
    const internalTok =
      typeof import.meta !== 'undefined' && import.meta.env?.DEV
        ? String(import.meta.env?.VITE_DEV_INTERNAL_API_TOKEN ?? '').trim()
        : '';
    if (internalTok) config.headers['X-Internal-Token'] = internalTok;
  }
  const hsciKey =
    typeof import.meta !== 'undefined' ? import.meta.env?.VITE_HSCI_AUTH_KEY : undefined;
  if (hsciKey && !internal) config.headers['x-hsci-auth-key'] = hsciKey;
  const url = resolvedRequestUrl(config);
  const envTenant =
    typeof import.meta !== 'undefined' ? import.meta.env?.VITE_TENANT_ID : undefined;
  if (!isAuthLoginOrRefreshUrl(url) && !requestAlreadyHasTenantId(config)) {
    const stored = tenantIdGetter?.()?.trim();
    if (stored) config.headers['X-Tenant-Id'] = stored;
    else if (envTenant) config.headers['X-Tenant-Id'] = envTenant;
  }
  const isLogin = /\/auth\/login(\?|$)/i.test(url);
  if (
    typeof import.meta !== 'undefined' &&
    import.meta.env?.DEV &&
    !warnedMissingTenant &&
    isLogin &&
    !requestAlreadyHasTenantId(config) &&
    !loginBodyHasWorkspaceSlug(config)
  ) {
    warnedMissingTenant = true;
    console.warn(
      '[@jn/api-client] Login request has no X-Tenant-Id and no workspaceSlug in body. Add the Workspace slug, ?tenantId=<uuid> from an owner invite, or set VITE_TENANT_ID for dev defaults on other routes.',
    );
  }
  return config;
});

// Response — normalise errors + handle 401
apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      tokenRefresher &&
      !isPublicAuthPath(originalRequest) &&
      !isInternalApiPath(originalRequest) &&
      authTokenGetter?.()
    ) {
      const denyCode = extractServerCode(error.response?.data)?.toUpperCase() ?? '';
      // Deactivated / suspended workspaces must not refresh their way back in.
      if (
        denyCode === 'DEACTIVATED' ||
        denyCode === 'ACCOUNT_DEACTIVATED' ||
        denyCode === 'TENANT_SUSPENDED'
      ) {
        // fall through to reject
      } else {
        originalRequest._retry = true;
        const newToken = await tokenRefresher();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }
    }

    const timedOut =
      error.code === 'ECONNABORTED' ||
      (typeof error.message === 'string' && error.message.toLowerCase().includes('timeout'));
    const baseUrl = resolveApiBaseUrl();
    const hint =
      timedOut &&
      (baseUrl.startsWith('/') || baseUrl.startsWith('.')) &&
      typeof import.meta !== 'undefined' &&
      import.meta.env?.DEV
        ? ' (dev: confirm the API responds on the expected host/port and path; a stuck handler or wrong route looks the same as a timeout.)'
        : '';

    const body = error.response?.data;
    const apiError: ApiError = {
      status:  error.response?.status ?? 0,
      code:
        extractServerCode(body) ??
        (body as Record<string, string> | undefined)?.code ??
        (timedOut ? 'TIMEOUT' : 'UNKNOWN_ERROR'),
      message:
        extractServerMessage(body) ??
        (timedOut
          ? `Request timed out after ${resolveTimeoutMs()}ms${hint}`
          : error.message),
      details: (body as Record<string, unknown> | undefined)?.details,
    };

    return Promise.reject(apiError);
  },
);

export { apiClient };
export { unwrapApiBody } from './unwrap';
